import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Header } from "@/components/header";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useServerFn } from "@tanstack/react-start";
import { createSession } from "@/lib/session.functions";
import { deleteQuiz } from "@/lib/quiz.functions";
import { Plus, Play, Trash2, Sparkles, BarChart3, MoreHorizontal, Eye, Edit3 } from "lucide-react";
import { useErrorReporter } from "@/components/error-reporter";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Route = createFileRoute("/_authenticated/dashboard")({
  component: Dashboard,
});

function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const startSession = useServerFn(createSession);
  const delQuiz = useServerFn(deleteQuiz);
  const { reportError } = useErrorReporter();

  async function load() {
    if (!user) return;
    const { data, error } = await supabase
      .from("quizzes")
      .select("*, questions(count)")
      .eq("creator_id", user.id)
      .order("created_at", { ascending: false });
    if (error) reportError(error, { area: "dashboard.load" });
    setQuizzes(data ?? []);
    setLoading(false);
  }
  useEffect(() => {
    load();
  }, [user?.id]);

  async function host(quizId) {
    try {
      const s = await startSession({ data: { quizId } });
      navigate({ to: "/host/$sessionId", params: { sessionId: s.id } });
    } catch (e) {
      reportError(e, { area: "session.create", retry: () => host(quizId) });
    }
  }
  async function remove(id) {
    if (!confirm("Delete this quiz?")) return;
    try {
      await delQuiz({ data: { id } });
      load();
    } catch (e) {
      // Previously unhandled: the rejection escaped with no user feedback at
      // all, so a failed delete looked like nothing happened.
      reportError(e, { area: "quiz.delete", retry: () => remove(id) });
    }
  }

  return (
    <div className="min-h-screen bg-bg">
      <Header />
      <div className="container mx-auto px-4 py-10 max-w-6xl">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">My Quizzes</h1>
            <p className="text-muted">Create, host, and analyze your quizzes.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline">
              <Link to="/quizzes/analytics">
                <BarChart3 className="size-4" /> Analytics
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/quizzes/new" search={{ ai: "1" }}>
                <Sparkles className="size-4" /> AI Generate
              </Link>
            </Button>
            <Button asChild className="bg-gradient-primary">
              <Link to="/quizzes/new">
                <Plus className="size-4" /> New quiz
              </Link>
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="p-5 bg-gradient-card border-2 h-[160px]" />
            ))}
          </div>
        ) : quizzes.length === 0 ? (
          <Card className="p-6 text-center bg-gradient-card border-2 sm:p-12">
            <Sparkles className="size-12 mx-auto text-accent" />
            <h2 className="text-xl font-bold mt-4">No quizzes yet</h2>
            <p className="text-muted mt-1">Generate one with AI in seconds or build manually.</p>
            <div className="mt-6 flex gap-2 justify-center">
              <Button asChild variant="outline">
                <Link to="/quizzes/new" search={{ ai: "1" }}>
                  AI Generate
                </Link>
              </Button>
              <Button asChild className="bg-gradient-primary">
                <Link to="/quizzes/new">Create manually</Link>
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {quizzes.map((q) => (
              <Card
                key={q.id}
                className="p-5 hover:shadow-elegant hover:-translate-y-0.5 transition-all bg-gradient-card border-2 group"
              >
                <div className="flex justify-between items-start gap-2">
                  <h3 className="font-bold text-lg line-clamp-2">{q.title}</h3>
                  <Badge variant={q.visibility === "public" ? "default" : "secondary"}>
                    {q.visibility}
                  </Badge>
                </div>
                <p className="text-sm text-muted line-clamp-2 mt-1">
                  {q.description ?? "No description"}
                </p>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-xs text-muted">{q.questions?.[0]?.count ?? 0} questions</p>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button
                    size="sm"
                    className="bg-gradient-primary flex-1"
                    onClick={() => host(q.id)}
                  >
                    <Play className="size-4" /> Host game
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="outline">
                        <MoreHorizontal className="size-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem asChild>
                        <Link to="/quizzes/$id/edit" params={{ id: q.id }}>
                          <Edit3 className="size-4 mr-2" /> Edit quiz
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/quizzes/$id/analytics" params={{ id: q.id }}>
                          <BarChart3 className="size-4 mr-2" /> View analytics
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => remove(q.id)} className="text-destructive">
                        <Trash2 className="size-4 mr-2" /> Delete quiz
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
